import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../lib/cn';

/**
 * Exported so a status chip can be applied to something that is not a `<span>`
 * — a link, say — without wrapping it.
 */
export const badgeVariants = cva(
  cn(
    'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5',
    'font-mono text-[0.68rem] uppercase leading-5 tracking-[0.1em]',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-3",
  ),
  {
    variants: {
      /**
       * Semantic, never decorative — the same tones the design language uses
       * everywhere else. A dataset still importing is `info`, one that failed
       * is `bad`, a plain label is `neutral`.
       *
       * `brand` (slice 65) is the one tone that is not a status: it names a
       * part of Wend — the pillar a Sense finding hands its action to, a pillar
       * that is on this workspace's plan. It is drawn in the brand's violet
       * precisely because that colour means "Wend" and never "good" or "bad",
       * so it cannot be misread as a verdict. The text is `brand-ink`, the
       * concept's pill ink (`.pill--brand`): `brand-b` itself is too dark to
       * read at this size on its own 18% wash. Until slice 66 the palette had
       * no lighter violet, so the hex was written here as an arbitrary value;
       * the Blueprint additions made it a token and this reads the token.
       * `border-brand-b/40 bg-brand-a/18` stay adjacent and in that order —
       * `verify-sense-screens` pins the pair beside the Today pillar badge.
       *
       * `ident-a` … `ident-d` (slice 66) are not statuses either. They name a
       * *kind* — a static list is `ident-a`, a cohort snapshot too — in an
       * identity colour from `lib/identity.ts`, so that "Static" does not
       * wear `info`'s cyan and read as a notice. The word inside is the
       * meaning; the colour only lets the eye find it again.
       */
      tone: {
        neutral: 'border-line-strong bg-white/[0.04] text-muted',
        good: 'border-good/40 bg-good/10 text-good',
        warn: 'border-warn/40 bg-warn/10 text-warn',
        bad: 'border-bad/45 bg-bad/10 text-bad',
        info: 'border-info/40 bg-info/10 text-info',
        brand: 'border-brand-b/40 bg-brand-a/18 text-brand-ink',
        'ident-a': 'border-ident-a/45 bg-ident-a/16 text-ident-a',
        'ident-b': 'border-ident-b/45 bg-ident-b/16 text-ident-b',
        'ident-c': 'border-ident-c/45 bg-ident-c/16 text-ident-c',
        'ident-d': 'border-ident-d/45 bg-ident-d/16 text-ident-d',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  },
);

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>;

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * A small filled circle before the text, in the badge's own colour —
   * "● Available". Decorative and `aria-hidden`: the word beside it is the
   * status, and a screen reader hearing "bullet" first learns nothing.
   *
   * Additive (slice 65). Without it the badge renders exactly the span it
   * always did, which matters because the admin app and several suites read
   * these class strings and this markup.
   */
  dot?: boolean;
}

/**
 * A small status chip. Colour carries meaning here, so it is never the only
 * carrier: the text inside says the status too.
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { className, tone, dot = false, children, ...rest },
  ref,
) {
  return (
    <span ref={ref} className={cn(badgeVariants({ tone }), className)} {...rest}>
      {dot && <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />}
      {children}
    </span>
  );
});
